import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import { mergeBudgetingGroups } from './utils/budgetingMerge';
import './BudgetingOverview.css';

const formatAmount = (value, digits = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('da-DK', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const niceMax = (value) => {
  if (!value || value <= 0) return 1;
  const padded = value * 1.1;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  return Math.ceil(padded / magnitude) * magnitude;
};

const BudgetingOverview = () => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byChassisType, setByChassisType] = useState({});
  const [chassisTypes, setChassisTypes] = useState([]);
  const [selectedChassisTypes, setSelectedChassisTypes] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);

  const fetchBudgeting = useCallback(async () => {
    if (!selectedTenant?.name) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/analytics/budgeting?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Failed to load budgeting data');
      }
      const types = Array.isArray(data.chassisTypes) ? data.chassisTypes : Object.keys(data.byChassisType || {});
      setByChassisType(data.byChassisType || {});
      setChassisTypes(types);
      setSelectedChassisTypes(types);
    } catch (err) {
      console.error('Error loading budgeting overview:', err);
      setError(err.message || 'Failed to load budgeting data');
      setByChassisType({});
      setChassisTypes([]);
      setSelectedChassisTypes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    fetchBudgeting();
  }, [fetchBudgeting]);

  const filtered = useMemo(() => {
    const selected = new Set(selectedChassisTypes);
    const groups = chassisTypes
      .filter((type) => selected.has(type))
      .map((type) => byChassisType[type])
      .filter(Boolean);
    return mergeBudgetingGroups(groups);
  }, [byChassisType, chassisTypes, selectedChassisTypes]);

  const totals = filtered.totals;
  const months = filtered.months;
  const byFrequency = filtered.byFrequency;

  const chart = useMemo(() => {
    const maxValue = niceMax(Math.max(0, ...months.map((month) => Number(month.total) || 0)));
    const width = 960;
    const height = 320;
    const padding = { top: 20, right: 16, bottom: 44, left: 72 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const slot = months.length > 0 ? plotWidth / months.length : plotWidth;
    const y = (value) => padding.top + plotHeight - (maxValue > 0 ? (value / maxValue) * plotHeight : 0);
    const points = months.map((month, index) => {
      const x = padding.left + slot * index + slot / 2;
      return { x, y: y(month.total), month };
    });
    const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
    const yTicks = [1, 0.75, 0.5, 0.25, 0].map((ratio) => ({
      value: maxValue * ratio,
      y: y(maxValue * ratio)
    }));
    return { maxValue, width, height, padding, plotHeight, slot, points, linePath, yTicks };
  }, [months]);

  const hovered = hoverIndex != null ? months[hoverIndex] : null;
  const allSelected = chassisTypes.length > 0 && selectedChassisTypes.length === chassisTypes.length;

  const toggleChassisType = (type) => {
    setSelectedChassisTypes((prev) => (
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
    ));
  };

  if (loading) {
    return <div className="budgeting-status">Loading budgeting overview...</div>;
  }

  if (error) {
    return <div className="budgeting-status budgeting-error">{error}</div>;
  }

  const hasCosts = totals && totals.pricedAssetCount > 0;

  return (
    <div className="budgeting-overview">
      {chassisTypes.length > 0 && (
        <div className="analytics-section budgeting-filter-section">
          <div className="section-header">
            <h3>Chassis Type</h3>
            <p>
              Choose which asset categories to include in the totals and timeline.
              {chassisTypes.includes('Licenses')
                ? ' Licenses use budgeting prices (unit × quantity) for the billing term.'
                : ''}
            </p>
          </div>
          <div className="budgeting-filter-actions">
            <button
              type="button"
              className="budgeting-filter-action"
              onClick={() => setSelectedChassisTypes(chassisTypes)}
              disabled={allSelected}
            >
              Show all
            </button>
            <button
              type="button"
              className="budgeting-filter-action"
              onClick={() => setSelectedChassisTypes([])}
              disabled={selectedChassisTypes.length === 0}
            >
              Clear
            </button>
          </div>
          <div className="budgeting-filter-chips">
            {chassisTypes.map((type) => {
              const active = selectedChassisTypes.includes(type);
              const typeTotals = byChassisType[type]?.totals;
              return (
                <button
                  key={type}
                  type="button"
                  className={`budgeting-filter-chip ${active ? 'active' : ''}`}
                  onClick={() => toggleChassisType(type)}
                  aria-pressed={active}
                >
                  <span>{type}</span>
                  {typeTotals && (
                    <span className="budgeting-filter-chip-meta">
                      {formatAmount(typeTotals.totalAssetCost, 0)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="budgeting-kpi-grid">
        <div className="budgeting-kpi">
          <div className="budgeting-kpi-label">Total asset cost</div>
          <div className="budgeting-kpi-value">{formatAmount(totals?.totalAssetCost, 2)}</div>
          <div className="budgeting-kpi-hint">
            Sum of priced items ({totals?.pricedAssetCount || 0})
            {selectedChassisTypes.length !== chassisTypes.length ? ' · filtered' : ''}
          </div>
        </div>
        <div className="budgeting-kpi">
          <div className="budgeting-kpi-label">Monthly recurring</div>
          <div className="budgeting-kpi-value">{formatAmount(totals?.monthlyRecurring, 2)}</div>
          <div className="budgeting-kpi-hint">
            Annualized run-rate from {totals?.recurringAssetCount || 0} items
          </div>
        </div>
        <div className="budgeting-kpi">
          <div className="budgeting-kpi-label">Annual recurring</div>
          <div className="budgeting-kpi-value">{formatAmount(totals?.annualRecurring, 2)}</div>
          <div className="budgeting-kpi-hint">Annualized from recurring frequencies</div>
        </div>
      </div>

      <div className="analytics-section budgeting-chart-section">
        <div className="section-header">
          <h3>12-month cost timeline</h3>
          <p>
            Expected cash due by month from current cost frequency. License costs use the catalogue price for the commitment term, billed on the billing term.
            Yearly costs use warranty end as the billing month.
          </p>
        </div>
        {!hasCosts ? (
          <div className="no-data">
            {selectedChassisTypes.length === 0
              ? 'Select at least one Chassis Type to view costs.'
              : 'No budgeting prices yet for the selected Chassis Types. Add price and cost frequency on Assets, or budgeting prices for Licenses.'}
          </div>
        ) : (
          <>
            <div className="budgeting-chart">
              <div
                className="budgeting-chart-plot"
                onMouseLeave={() => setHoverIndex(null)}
              >
              <svg
                className="budgeting-svg"
                viewBox={`0 0 ${chart.width} ${chart.height}`}
                role="img"
                aria-label="Expected asset cost by month for the next 12 months"
              >
                {chart.yTicks.map((tick) => (
                  <g key={tick.value}>
                    <line
                      x1={chart.padding.left}
                      x2={chart.width - chart.padding.right}
                      y1={tick.y}
                      y2={tick.y}
                      className="budgeting-grid"
                    />
                    <text x={chart.padding.left - 10} y={tick.y + 4} textAnchor="end" className="budgeting-axis">
                      {formatAmount(tick.value)}
                    </text>
                  </g>
                ))}
                {months.map((month, index) => {
                  const barWidth = Math.max(8, chart.slot * 0.45);
                  const x = chart.padding.left + chart.slot * index + (chart.slot - barWidth) / 2;
                  const recurringHeight = chart.maxValue > 0 ? (month.recurring / chart.maxValue) * chart.plotHeight : 0;
                  const oneTimeHeight = chart.maxValue > 0 ? (month.oneTime / chart.maxValue) * chart.plotHeight : 0;
                  const base = chart.padding.top + chart.plotHeight;
                  return (
                    <g
                      key={month.key}
                      onMouseEnter={() => setHoverIndex(index)}
                    >
                      <rect
                        x={chart.padding.left + chart.slot * index}
                        y={chart.padding.top}
                        width={chart.slot}
                        height={chart.plotHeight}
                        className="budgeting-hit"
                      />
                      {recurringHeight > 0 && (
                        <rect
                          x={x}
                          y={base - recurringHeight}
                          width={barWidth}
                          height={Math.max(recurringHeight, 0)}
                          rx="3"
                          className="budgeting-rect recurring"
                        />
                      )}
                      {oneTimeHeight > 0 && (
                        <rect
                          x={x}
                          y={base - recurringHeight - oneTimeHeight}
                          width={barWidth}
                          height={Math.max(oneTimeHeight, 0)}
                          rx="3"
                          className="budgeting-rect onetime"
                        />
                      )}
                      <text
                        x={chart.padding.left + chart.slot * index + chart.slot / 2}
                        y={chart.height - 14}
                        textAnchor="middle"
                        className="budgeting-axis"
                      >
                        {month.label}
                      </text>
                    </g>
                  );
                })}
                {chart.linePath && (
                  <path d={chart.linePath} className="budgeting-line" fill="none" />
                )}
                {chart.points.map((point, index) => (
                  <circle
                    key={point.month.key}
                    cx={point.x}
                    cy={point.y}
                    r={hoverIndex === index ? 5 : 3.5}
                    className="budgeting-point"
                    onMouseEnter={() => setHoverIndex(index)}
                  />
                ))}
              </svg>
              {hovered && (
                <div
                  className={`budgeting-month-tooltip${hoverIndex >= months.length - 3 ? ' align-end' : hoverIndex <= 1 ? ' align-start' : ''}`}
                  style={{
                    left: `${((chart.padding.left + chart.slot * hoverIndex + chart.slot / 2) / chart.width) * 100}%`
                  }}
                >
                  <div className="budgeting-month-tooltip-header">
                    <div className="budgeting-month-tooltip-title">{hovered.label}</div>
                    <div className="budgeting-month-tooltip-total">{formatAmount(hovered.total, 2)}</div>
                  </div>
                  {hovered.recurring > 0 || hovered.oneTime > 0 ? (
                    <div className="budgeting-month-tooltip-meta">
                      {hovered.recurring > 0 ? `Recurring ${formatAmount(hovered.recurring, 2)}` : ''}
                      {hovered.recurring > 0 && hovered.oneTime > 0 ? ' · ' : ''}
                      {hovered.oneTime > 0 ? `One-time ${formatAmount(hovered.oneTime, 2)}` : ''}
                    </div>
                  ) : null}
                  {(hovered.items || []).length === 0 ? (
                    <div className="budgeting-month-tooltip-empty">No products due this month</div>
                  ) : (
                    <ul className="budgeting-month-tooltip-list">
                      {(hovered.items || []).map((item, itemIndex) => (
                        <li key={`${item.name}-${item.kind}-${itemIndex}`}>
                          <span className="budgeting-month-tooltip-name">{item.name}</span>
                          <span className="budgeting-month-tooltip-amount">{formatAmount(item.amount, 2)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              </div>
            </div>
            <div className="budgeting-chart-footer">
              <div className="budgeting-legend">
                <span className="budgeting-swatch recurring" /> Recurring due
                <span className="budgeting-swatch onetime" /> One-time
              </div>
            </div>
          </>
        )}
      </div>

      {byFrequency.length > 0 && (
        <div className="analytics-section budgeting-table-section">
          <div className="section-header">
            <h3>Cost by frequency</h3>
            <p>How budgeting prices convert to monthly and annual run-rate. License rows already include SKU quantity and billing versus commitment terms.</p>
          </div>
          <div className="budgeting-table-wrap">
            <table className="budgeting-table">
              <thead>
                <tr>
                  <th>Frequency</th>
                  <th>Assets</th>
                  <th>Budgeting price</th>
                  <th>Monthly recurring</th>
                  <th>Annual recurring</th>
                </tr>
              </thead>
              <tbody>
                {byFrequency.map((row) => (
                  <tr key={row.frequency}>
                    <td>{row.frequency}</td>
                    <td>{row.count}</td>
                    <td>{formatAmount(row.totalPrice, 2)}</td>
                    <td>{formatAmount(row.monthlyRecurring, 2)}</td>
                    <td>{formatAmount(row.annualRecurring, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetingOverview;
